/**
 * SMS Service
 *
 * Client-side service for Twilio SMS functionality
 *
 * Features:
 * - Send SMS messages to borrowers
 * - Provision/release Twilio numbers
 * - Format phone numbers to E.164
 * - Validate phone numbers
 * - Create pre-approval SMS templates
 */

import { supabase } from '@/utils/supabase';
import { PreApprovalData } from '@/types/pre-approval';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('SmsService', 'sms');

// Interfaces
export interface SendSmsParams {
  from: string;                // Loan officer's Twilio number
  to: string;                  // Borrower's phone
  message: string;             // SMS body
  conversationId?: string;     // Optional: existing conversation
  contextType?: 'pre_approval' | 'general';
  contextId?: string;          // Related entity ID
  loanOfficerId: string;       // Current user ID
  recipientType?: 'borrower' | 'partner' | 'co_borrower';  // Recipient type for consent bypass
}

export interface SendSmsResult {
  success: boolean;
  messageSid?: string;
  communicationId?: string;
  conversationId?: string;
  error?: string;
}

// Number pool management interfaces
export interface ListAvailableNumbersParams {
  areaCode?: string;
  searchTwilio?: boolean;
  limit?: number;
  targetUserId?: string;
}

export interface PooledNumber {
  id: string;
  phoneNumber: string;
  twilioSid: string;
  areaCode: string;
  locality: string;
  region: string;
  assignmentCount: number;
  releasedAt: string | null;
  lastAssignedTo: { name: string; email: string } | null;
}

export interface TwilioSearchResult {
  phoneNumber: string;
  friendlyName: string;
  locality: string;
  region: string;
  numberType?: string;  // 'local' | 'mobile'
  capabilities: { sms: boolean; mms: boolean; voice: boolean };
}

export interface ListAvailableNumbersResult {
  success: boolean;
  pooledNumbers?: PooledNumber[];
  twilioSearchResults?: TwilioSearchResult[];
  poolSummary?: { total: number; available: number; assigned: number };
  error?: string;
}

export interface ProvisionNumberParams {
  areaCode?: string;           // Optional: filter by area code
  specificNumber?: string;     // Optional: purchase specific number
  targetUserId?: string;
}

export interface ProvisionNumberResult {
  success: boolean;
  phoneNumber?: string;
  twilioSid?: string;
  poolId?: string;
  areaCode?: string;
  locality?: string;
  region?: string;
  error?: string;
}

export interface AssignNumberParams {
  userId: string;
  phoneNumber: string;  // Number from pool to assign
}

export interface AssignNumberResult {
  success: boolean;
  phoneNumber?: string;
  twilioSid?: string;
  error?: string;
}

export interface UnassignNumberResult {
  success: boolean;
  phoneNumber?: string;
  message?: string;
  error?: string;
}

export interface SyncNumbersResult {
  success: boolean;
  synced: number;
  updated: number;
  skipped: number;
  total: number;
  numbers: {
    phoneNumber: string;
    action: 'imported' | 'updated' | 'skipped';
    reason?: string;
  }[];
  error?: string;
}

/**
 * SMS Service Class
 */
export class SmsService {
  /**
   * Format phone number to E.164 format
   * Examples:
   *  - "3035551234" → "+13035551234"
   *  - "(303) 555-1234" → "+13035551234"
   *  - "1-303-555-1234" → "+13035551234"
   */
  static formatToE164(phone: string): string {
    if (!phone) {
return '';
}

    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // If already has 1 and 11 digits, add +
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    // If 10 digits, add +1
    if (digits.length === 10) {
      return `+1${digits}`;
    }

    // Default: add +1
    return `+1${digits}`;
  }

  /**
   * Validate US phone number
   * Returns true if phone number is valid 10 or 11 digit US number
   */
  static validateUSPhone(phone: string): boolean {
    if (!phone) {
return false;
}

    const digits = phone.replace(/\D/g, '');

    // Must be 10 digits (without country code) or 11 digits (with 1)
    if (digits.length === 10) {
      return true;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return true;
    }

    return false;
  }

  /**
   * Parse an error returned by supabase.functions.invoke().
   * supabase-js v2 attaches the original Response on FunctionsHttpError.context;
   * the body's `error` field carries the specific server message (e.g., TCPA window).
   */
  static async parseInvokeError(error: unknown, fallback: string): Promise<string> {
    const context = error && typeof error === 'object' && 'context' in error
      ? (error as { context?: unknown }).context
      : undefined;

    const hasResponseShape = context !== undefined
      && typeof context === 'object'
      && context !== null
      && typeof (context as { clone?: unknown }).clone === 'function';

    if (hasResponseShape) {
      try {
        const body = await (context as Response).clone().json();
        if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
          return (body as { error: string }).error;
        }
      } catch {
        // malformed body — fall through
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }
    return fallback;
  }

  /**
   * Send SMS message via Twilio
   */
  static async sendSms(params: SendSmsParams): Promise<SendSmsResult> {
    try {
      log.info('Sending SMS', {
        to: params.to,
        from: params.from,
        contextType: params.contextType
      });

      // Format phone numbers
      const fromE164 = this.formatToE164(params.from);
      const toE164 = this.formatToE164(params.to);

      // Validate phone numbers
      if (!this.validateUSPhone(toE164)) {
        return {
          success: false,
          error: 'Invalid recipient phone number'
        };
      }

      // Call Twilio SMS Edge Function. Server enforces TCPA consent + time window;
      // surface the body's error string when the function returns non-2xx.
      const { data, error } = await supabase.functions.invoke('twilio-sms', {
        body: {
          from: fromE164,
          to: toE164,
          message: params.message,
          conversationId: params.conversationId,
          contextType: params.contextType,
          contextId: params.contextId,
          loanOfficerId: params.loanOfficerId,
          recipientType: params.recipientType
        }
      });

      if (error) {
        const message = await SmsService.parseInvokeError(error, 'Failed to send SMS');
        log.error('SMS sending failed', { message }, error);
        return { success: false, error: message };
      }

      log.info('SMS sent successfully', {
        messageSid: data.messageSid,
        conversationId: data.conversationId
      });

      return {
        success: true,
        messageSid: data.messageSid,
        communicationId: data.communicationId,
        conversationId: data.conversationId
      };

    } catch (error) {
      log.error('SMS service error', {}, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send SMS'
      };
    }
  }

  /**
   * Send pre-approval SMS
   * Formats a professional pre-approval notification message
   */
  static async sendPreApprovalSMS(
    preApproval: PreApprovalData,
    borrowerPhone: string,
    customMessage: string,
    accessUrl: string,
    loanOfficer: {
      id: string;
      businessSmsNumber: string;
      firstName: string;
      lastName: string;
    },
    recipientType: 'borrower' | 'partner' | 'co_borrower' = 'borrower'
  ): Promise<SendSmsResult> {
    try {
      // Phone-keyed consent pre-flight for borrower/co-borrower (server is the authoritative gate).
      // Partners are exempt from consent requirement.
      if (recipientType !== 'partner') {
        const consent = await this.checkSmsConsentByPhone(this.formatToE164(borrowerPhone));
        if (!consent.granted) {
          log.warn('SMS consent not granted for recipient phone', {
            recipientType,
            preApprovalId: preApproval.id
          });
          return {
            success: false,
            error: 'SMS consent not granted. Please obtain consent before sending SMS messages.'
          };
        }
      }

      // Create SMS template
      const template = this.createPreApprovalTemplate(
        preApproval,
        customMessage,
        accessUrl,
        loanOfficer.firstName
      );

      log.info('Sending pre-approval SMS', {
        preApprovalId: preApproval.id,
        borrowerPhone,
        recipientType
      });

      // Send SMS
      return await this.sendSms({
        from: loanOfficer.businessSmsNumber,
        to: borrowerPhone,
        message: template,
        contextType: 'pre_approval',
        contextId: preApproval.id,
        loanOfficerId: loanOfficer.id,
        recipientType
      });

    } catch (error) {
      log.error('Pre-approval SMS failed', {}, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send pre-approval SMS'
      };
    }
  }

  /**
   * Create pre-approval SMS template
   * Simple format: custom message + access link + opt-out
   */
  static createPreApprovalTemplate(
    preApproval: PreApprovalData,
    customMessage: string,
    accessUrl: string,
    _loanOfficerFirstName: string
  ): string {
    // Build simple message
    let message = '';

    // Add custom message if provided
    if (customMessage && customMessage.trim()) {
      message += `${customMessage.trim()}\n\n`;
    }

    // Add access link
    message += `View: ${accessUrl}\n\n`;

    // Add opt-out (legally required)
    message += `Reply STOP to opt-out`;

    return message;
  }

  /**
   * List available numbers from pool and optionally search Twilio
   */
  static async listAvailableNumbers(
    params?: ListAvailableNumbersParams
  ): Promise<ListAvailableNumbersResult> {
    try {
      log.info('Listing available numbers', params);

      const { data, error } = await supabase.functions.invoke('twilio-list-available-numbers', {
        body: {
          areaCode: params?.areaCode,
          searchTwilio: params?.searchTwilio,
          limit: params?.limit,
          targetUserId: params?.targetUserId
        }
      });

      if (error) {
        log.error('Failed to list numbers', {}, error);
        throw new Error(await SmsService.parseInvokeError(error, 'Failed to list numbers'));
      }

      log.info('Numbers listed successfully', {
        poolCount: data.pooledNumbers?.length,
        twilioCount: data.twilioSearchResults?.length
      });

      return {
        success: true,
        pooledNumbers: data.pooledNumbers,
        twilioSearchResults: data.twilioSearchResults,
        poolSummary: data.poolSummary
      };

    } catch (error) {
      log.error('List numbers error', {}, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to list numbers'
      };
    }
  }

  /**
   * Provision a new number from Twilio and add to pool
   * Does NOT assign to user - use assignNumber() for that
   */
  static async provisionNumber(params?: ProvisionNumberParams): Promise<ProvisionNumberResult> {
    try {
      log.info('Provisioning new Twilio number', params);

      const { data, error } = await supabase.functions.invoke('twilio-provision-number', {
        body: {
          areaCode: params?.areaCode,
          specificNumber: params?.specificNumber,
          targetUserId: params?.targetUserId
        }
      });

      if (error) {
        log.error('Number provisioning failed', {}, error);
        throw new Error(await SmsService.parseInvokeError(error, 'Failed to provision number'));
      }

      log.info('Number provisioned successfully', {
        phoneNumber: data.phoneNumber,
        poolId: data.poolId
      });

      return {
        success: true,
        phoneNumber: data.phoneNumber,
        twilioSid: data.twilioSid,
        poolId: data.poolId,
        areaCode: data.areaCode,
        locality: data.locality,
        region: data.region
      };

    } catch (error) {
      log.error('Provision number error', {}, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to provision number'
      };
    }
  }

  /**
   * Assign a number from pool to a loan officer
   * Number must already exist in pool (use provisionNumber() first if needed)
   */
  static async assignNumber(params: AssignNumberParams): Promise<AssignNumberResult> {
    try {
      log.info('Assigning number from pool', {
        userId: params.userId,
        phoneNumber: params.phoneNumber
      });

      const { data, error } = await supabase.functions.invoke('twilio-assign-number', {
        body: {
          userId: params.userId,
          phoneNumber: params.phoneNumber
        }
      });

      if (error) {
        log.error('Number assignment failed', {}, error);
        throw new Error(await SmsService.parseInvokeError(error, 'Failed to assign number'));
      }

      log.info('Number assigned successfully', {
        phoneNumber: data.phoneNumber
      });

      return {
        success: true,
        phoneNumber: data.phoneNumber,
        twilioSid: data.twilioSid
      };

    } catch (error) {
      log.error('Assign number error', {}, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to assign number'
      };
    }
  }

  /**
   * Unassign a number (returns it to pool for reuse)
   */
  static async unassignNumber(userId: string, reason?: string): Promise<UnassignNumberResult> {
    try {
      log.info('Unassigning number', { userId, reason });

      const { data, error } = await supabase.functions.invoke('twilio-unassign-number', {
        body: {
          userId,
          reason
        }
      });

      if (error) {
        log.error('Number unassignment failed', {}, error);
        throw new Error(await SmsService.parseInvokeError(error, 'Failed to unassign number'));
      }

      log.info('Number returned to pool', {
        phoneNumber: data.phoneNumber
      });

      return {
        success: true,
        phoneNumber: data.phoneNumber,
        message: data.message
      };

    } catch (error) {
      log.error('Unassign number error', {}, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to unassign number'
      };
    }
  }

  /**
   * Sync Twilio numbers to pool
   * Imports existing owned Twilio numbers into the number pool
   */
  static async syncTwilioNumbers(): Promise<SyncNumbersResult> {
    try {
      log.info('Syncing Twilio numbers to pool');

      const { data, error } = await supabase.functions.invoke('twilio-sync-numbers', {
        body: {}
      });

      if (error) {
        log.error('Twilio sync failed', {}, error);
        throw new Error(await SmsService.parseInvokeError(error, 'Failed to sync Twilio numbers'));
      }

      log.info('Twilio numbers synced successfully', {
        synced: data.synced,
        updated: data.updated,
        skipped: data.skipped,
        total: data.total
      });

      return {
        success: true,
        synced: data.synced,
        updated: data.updated,
        skipped: data.skipped,
        total: data.total,
        numbers: data.numbers
      };

    } catch (error) {
      log.error('Sync Twilio numbers error', {}, error);
      return {
        success: false,
        synced: 0,
        updated: 0,
        skipped: 0,
        total: 0,
        numbers: [],
        error: error instanceof Error ? error.message : 'Failed to sync Twilio numbers'
      };
    }
  }

  /**
   * Check if borrower has SMS consent
   * Returns true if consent exists and is active
   */
  static async checkSmsConsent(borrowerId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('borrower_consents')
        .select('granted')
        .eq('borrower_id', borrowerId)
        .eq('consent_type', 'sms')
        .eq('granted', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned (no consent)
        log.error('Consent check failed', {}, error);
        return false;
      }

      return !!data?.granted;

    } catch (error) {
      log.error('Consent check error', {}, error);
      return false;
    }
  }

  /**
   * Check active SMS consent by phone (E.164). Matches the server check in
   * supabase/functions/twilio-sms/index.ts:377–439 — phone + sms + granted=true,
   * then expiration is verified in-row.
   */
  static async checkSmsConsentByPhone(phoneE164: string): Promise<{
    granted: boolean;
    id?: string;
    grantedAt?: string;
    expiresAt?: string;
  }> {
    try {
      const { data } = await supabase
        .from('borrower_consents')
        .select('id, granted, granted_at, consent_expires_at')
        .eq('borrower_phone', phoneE164)
        .eq('consent_type', 'sms')
        .eq('granted', true)
        .maybeSingle();

      if (!data) {
        return { granted: false };
      }
      if (data.consent_expires_at && new Date(data.consent_expires_at) < new Date()) {
        return { granted: false };
      }
      return {
        granted: true,
        id: data.id,
        grantedAt: data.granted_at,
        expiresAt: data.consent_expires_at
      };
    } catch (error) {
      log.error('Consent-by-phone check error', {}, error);
      return { granted: false };
    }
  }

  /**
   * Record SMS consent
   */
  static async recordSmsConsent(borrowerId: string, ipAddress?: string, method?: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('borrower_consents')
        .upsert({
          borrower_id: borrowerId,
          consent_type: 'sms',
          granted: true,
          granted_at: new Date().toISOString(),
          ip_address: ipAddress || null,
          method: method || 'web_form'
        });

      if (error) {
        log.error('Failed to record consent', {}, error);
        return false;
      }

      log.info('SMS consent recorded', { borrowerId });
      return true;

    } catch (error) {
      log.error('Consent recording error', {}, error);
      return false;
    }
  }
}

// Export default
export default SmsService;
